import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import type React from 'react';
import type { Tender } from '../../types/tender';

interface TenderHeaderProps {
  tender: Tender;
  isPartnerView: boolean;
  onBackClick: () => void;
}

const TenderHeader: React.FC<TenderHeaderProps> = ({ tender, isPartnerView, onBackClick }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 4,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      <IconButton onClick={onBackClick} sx={{ mr: 2 }}>
        <ArrowBackIcon />
      </IconButton>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              wordBreak: 'break-word',
              fontSize: { xs: '1.2rem', md: '1.5rem' },
            }}
          >
            {tender.title || 'Tender Details'}
          </Typography>
          {/* INTERIM WHITE-LABELING: Grant Indicator */}
          {tender.isGrant && (
            <Chip
              label="Grant Mode Active"
              color="success"
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 'bold',
                borderWidth: 2,
                backgroundColor: (theme) => `${theme.palette.success.main}1A`,
              }}
            />
          )}
          {tender.isComplex && (
            <Tooltip
              title="This Request has a massive amount of attachments. The AI may not have been able to read every single file due to token limits. Please review the 'Capability Analysis' report."
              slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
              arrow
            >
              <Chip
                label="Complex"
                color="warning"
                size="medium"
                sx={{ fontWeight: 'bold', cursor: 'help' }}
              />
            </Tooltip>
          )}
          {!isPartnerView &&
            (() => {
              const linkedPartnersCount =
                tender.alignmentAnalysis?.recommendedPartners?.filter(
                  (p) => p.userId && tender.sharedWith?.includes(p.userId)
                ).length || 0;

              if (linkedPartnersCount === 0) {
                return null;
              }

              return (
                <Chip
                  label={`${linkedPartnersCount} Partner${linkedPartnersCount !== 1 ? 's' : ''} Linked`}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    backgroundColor: (theme) => `${theme.palette.primary.main}1A`,
                  }}
                />
              );
            })()}
        </Box>
        <Typography variant="subtitle1" color="text.primary">
          {/* INTERIM WHITE-LABELING: Dynamic Text */}
          Follow the steps below to generate your {tender.isGrant ? 'grant' : 'tender'} response.
        </Typography>
      </Box>
    </Box>
  );
};

export default TenderHeader;
